import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { humanizeError } from '@/lib/errors';
import { needsSave, snapshot } from '@/lib/lessonSaveDiff';
import { blockFailure, type BlockFailure } from '@/lib/lessonBuilderErrors';
import { blockGuardIssue, issueNumbers } from '@/lib/quizCorrectGuard';
import { applyFixes, codeOf, needsHumanLook, scanContent, splitTwins, willBeFixed } from '@/lib/mixedScript';
import { needsReload, transferBlocker, type TransferOp } from '@/lib/contentTransfer';
import { titleHtml, titleText } from '@/lib/blockTitle';
import { coursesApi, type Block, type BlockType, type Lesson } from '@/api/courses';
import { BlockView } from '@/components/lesson/BlockView';
import { BlockBoundary } from '@/components/lesson/BlockBoundary';
import { SectionsNav } from '@/components/lesson/SectionsNav';
import { computeNumbers } from '@/components/lesson/blockNumbering';

import { blockDefForBlock } from './blockDefForBlock';
import { blockDefByPick, blockRegistry, paletteKey, useBlockCategories, RichTextEditor, type BlockCategory } from './blockRegistry';
import { TransferDialog, TransferMenu } from './TransferDialog';
import { StudentPreview } from './StudentPreview';
import { toPreviewSections } from '@/lib/lessonPreview';
import { UNDO_MS, putBack, removalServerIds, takeBlock, type PendingRemoval } from '@/lib/lessonUndo';
import { useConfirm } from '@/components/ui/ConfirmProvider';

/**
 * Lesson Builder — Edvibe-style WYSIWYG editor.
 *
 * Layout (immersive, full-width):
 *   ┌───────────────────────────────────────────────────────────┐
 *   │  ← Назад            [Сохранение…]      Превью   Сохранить    │
 *   │  «режим предпросмотра и редактирования интерактивного урока» │
 *   ├──────────┬───────────────────────────────┬─────────────────┤
 *   │ palette  │   active section canvas        │   Разделы        │
 *   │ (icons)  │   title + real blocks + "+"    │   + homework     │
 *   └──────────┴───────────────────────────────┴─────────────────┘
 *
 * Blocks render through the SAME `BlockView` the student sees (mode="preview"),
 * so the canvas is a true preview. Configuration of a block reuses the existing
 * per-type `renderEditor` form, shown inline under the preview (the hybrid: real
 * look, full editability, without rewriting all 27 types at once).
 */

// Block types whose editor is a plain text field — edited directly on the block
// (Edvibe-style), no preview/Configure toggle.
const INLINE_TYPES: ReadonlySet<BlockType> = new Set<BlockType>(['text', 'article', 'callout']);

type DraftBlock = Pick<Block, 'id' | 'type' | 'content' | 'order_index'> & { serverId?: string };
type DraftSection = {
  id: string;
  serverId?: string;
  title: string;
  is_homework?: boolean;
  blocks: DraftBlock[];
};

export function LessonBuilderPage() {
  const { t } = useTranslation();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [lessonTitle, setLessonTitle] = useState(t('lessonBuilder.newLesson'));
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [deletedSectionIds, setDeletedSectionIds] = useState<string[]>([]);
  const [deletedBlockIds, setDeletedBlockIds] = useState<string[]>([]);
  /** Блок убран с экрана, но ещё можно «Отменить» (lib/lessonUndo.ts). */
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval<DraftBlock> | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const ask = useConfirm();
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [genOpen, setGenOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'unsaved' | 'saving' | 'saved'>('idle');
  /**
   * Отказы сервера по каждому блоку — показываются НА САМОМ блоке, а не одной
   * полосой наверху: «В тесте не отмечен верный ответ» без указания блока в уроке
   * на сорок заданий не помогает совсем.
   */
  const [blockFailures, setBlockFailures] = useState<Record<string, BlockFailure>>({});

  const lessonQ = useQuery({
    queryKey: ['admin', 'lesson', lessonId],
    queryFn: () => coursesApi.getFullLesson(lessonId!),
    enabled: !!lessonId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  /**
   * Урок принадлежит ОБРАЗЦУ?
   *
   * ⚠️ Спрашиваем курс, а не урок: признак образца живёт у курса. Запрос идёт
   * только когда номер курса известен — у нового, ещё не сохранённого урока его
   * нет, и лишний запрос вернул бы ошибку.
   */
  const marathonId = lessonQ.data?.marathon_id ?? undefined;
  const marathonQ = useQuery({
    queryKey: ['admin', 'marathon-of-lesson', marathonId],
    queryFn: () => coursesApi.getMarathon(marathonId!),
    enabled: !!marathonId,
    staleTime: 60_000,
  });
  const isTemplate = marathonQ.data?.is_template === true;

  const [applying, setApplying] = useState(false);

  /**
   * Раскатать правки образца по созданным из него курсам.
   *
   * ⚠️ С 28.09.2026 — БЕЗ СОХРАНЕНИЯ, НО С ВОПРОСОМ (разбор удобства админки №6). Прежде здесь сперва
   * сохранялся урок, чтобы не уехала прошлая версия. Но образец закрыт для правок на сервере (07.09,
   * `ЗапретПравкиОбразца`, отказ 423 `template_read_only`): сохранять в нём нечего, и `handleSave` лишь сыпал
   * отказами. Зато раскатка уходила по одному нажатию, без вопроса, во все курсы сразу — теперь сперва
   * подтверждение с числом курсов, тем же текстом, что в списке курсов.
   */
  const handleApplyToCourses = async () => {
    if (!marathonId) return;
    setSaveError(null);
    const courses = await coursesApi.templateUsage(marathonId).catch(() => null);
    const agreed = await ask({
      title: t('adminMarathons.syncTitle'),
      message:
        courses === null
          ? t('adminMarathons.syncBody')
          : `${t('lessonBuilder.applyCourses', { count: courses })} ${t('adminMarathons.syncBody')}`,
      confirmLabel: t('adminMarathons.syncConfirm'),
    });
    if (!agreed) return;
    setApplying(true);
    try {
      const r = await coursesApi.syncFromTemplate(marathonId);
      await ask({
        title: t('lessonBuilder.applyToCourses'),
        message: t('lessonBuilder.applyDone', {
          courses: r.courses,
          updated: r.updated,
          added: r.added,
          kept: r.kept,
        }),
        notice: true,
      });
    } catch (e) {
      setSaveError(humanizeError(e));
    } finally {
      setApplying(false);
    }
  };

  const hydratedLessonId = useRef<string | null>(null);

  /**
   * Что уже лежит на сервере — снимок на каждый узел (`blk:<id>` и `sec:<id>`).
   *
   * ⚠️ ЗАЧЕМ. Без него сохранение слало `PUT` на ВСЕ блоки урока подряд, менялись они
   * или нет: урок на сорок заданий — сорок запросов каждые 2,5 с правки, и каждый будит
   * проход зеркала подтипов (техдолг п. 178).
   *
   * ⚠️ Снимок — ТОЛЬКО о удачной записи. Отказ его не обновляет, иначе неуехавшая
   * правка считалась бы сохранённой и не ушла бы уже никогда.
   */
  const savedSnapRef = useRef(new Map<string, string | null>());

  useEffect(() => {
    setSections([]);
    setDeletedSectionIds([]);
    setDeletedBlockIds([]);
    setPendingRemoval(null);
    setActiveSectionId(null);
    setEditingBlockId(null);
    setSaveStatus('idle');
    setBlockFailures({});
    savedSnapRef.current.clear();
    hydratedLessonId.current = null;
  }, [lessonId]);

  /**
   * Разложить урок с сервера в черновик конструктора.
   *
   * ⚠️ ЗОВЁТСЯ ИЗ ДВУХ МЕСТ: при открытии урока и после переноса блока или раздела.
   * Перенос не может просто сбросить `hydratedLessonId` и ждать эффекта: если урок
   * пришёл тем же по содержимому, react-query отдаёт тот же объект, эффект не
   * срабатывает, а сброшенная метка остаётся — и следующее автосохранение
   * перечитало бы урок поверх набранного методистом. Поэтому раскладываем явно.
   *
   * Функция лежит в ref, как `saveRef` ниже: эффекту нужна свежая версия, а в
   * зависимостях её держать нельзя — она новая на каждой отрисовке.
   *
   * `keepSectionId` — раздел, который оставить открытым, если он ещё в уроке.
   */
  const hydrateRef = useRef<(data: Lesson, keepSectionId?: string | null) => void>(() => {});
  hydrateRef.current = (data, keepSectionId) => {
    hydratedLessonId.current = lessonId ?? null;
    setLessonTitle(data.title);
    const serverSections: DraftSection[] = (data.sections ?? []).map((s) => ({
      id: s.id,
      serverId: s.id,
      title: s.title,
      is_homework: s.is_homework,
      blocks: (s.blocks ?? []).map((b) => ({
        id: b.id,
        serverId: b.id,
        type: b.type,
        content: b.content,
        order_index: b.order_index,
      })),
    }));
    // Всё, что пришло с сервера, уже сохранено — с этого снимка и считаем правки.
    savedSnapRef.current.clear();
    serverSections.forEach((s, i) => {
      savedSnapRef.current.set(
        `sec:${s.serverId}`,
        snapshot({ title: s.title, order_index: i, is_homework: s.is_homework ?? false }),
      );
      s.blocks.forEach((b, j) => {
        savedSnapRef.current.set(`blk:${b.serverId}`, snapshot({ type: b.type, content: b.content, order_index: j }));
      });
    });
    const seeded = serverSections.length
      ? serverSections
      : [{ id: `sec-${Date.now()}`, title: `${t('lessonBuilder.sectionDefault')} 1`, blocks: [] }];
    setSections(seeded);
    setActiveSectionId(seeded.find((s) => s.id === keepSectionId)?.id ?? seeded[0]?.id ?? null);
  };

  useEffect(() => {
    if (!lessonQ.data) return;
    if (hydratedLessonId.current === lessonId) return;
    hydrateRef.current(lessonQ.data);
  }, [lessonQ.data, lessonId]);

  // ---------- dirty tracking + debounced autosave ----------
  const [dirtyTick, setDirtyTick] = useState(0);
  /** Число правок — синхронно, без отрисовки: `handleSave` сверяет его до и после прохода. */
  const editsRef = useRef(0);
  const markDirty = () => {
    editsRef.current += 1;
    setSaveStatus('unsaved');
    setDirtyTick((n) => n + 1);
  };

  // ---------- section / block mutators ----------
  const addSection = () => {
    const id = `sec-${Date.now()}`;
    setSections((s) => [...s, { id, title: `${t('lessonBuilder.sectionDefault')} ${s.filter((x) => !x.is_homework).length + 1}`, blocks: [] }]);
    setActiveSectionId(id);
    markDirty();
  };

  const addHomeworkSection = () => {
    const existing = sections.find((s) => s.is_homework);
    if (existing) {
      setActiveSectionId(existing.id);
      return;
    }
    const id = `sec-${Date.now()}`;
    setSections((s) => [...s, { id, title: t('lessonBuilder.addHomework'), is_homework: true, blocks: [] }]);
    setActiveSectionId(id);
    markDirty();
  };

  const renameSection = (sectionId: string, title: string) => {
    setSections((s) => s.map((sec) => (sec.id === sectionId ? { ...sec, title } : sec)));
    markDirty();
  };

  const removeSection = (sectionId: string) => {
    setSections((s) => {
      const target = s.find((sec) => sec.id === sectionId);
      if (target?.serverId) setDeletedSectionIds((d) => [...d, target.serverId!]);
      const next = s.filter((sec) => sec.id !== sectionId);
      if (sectionId === activeSectionId) setActiveSectionId(next[0]?.id ?? null);
      return next;
    });
    markDirty();
  };

  // Drag-and-drop reorder of the numbered sections. `toIdx` is the index within
  // the non-homework list; the homework section always stays pinned at the end.
  // Persisted on the next autosave, which renumbers order_index by array position.
  const reorderSection = (fromId: string, toIdx: number) => {
    setSections((s) => {
      const normal = s.filter((x) => !x.is_homework);
      const homework = s.filter((x) => x.is_homework);
      const from = normal.findIndex((x) => x.id === fromId);
      if (from < 0 || toIdx < 0 || toIdx >= normal.length || from === toIdx) return s;
      const next = [...normal];
      const [moved] = next.splice(from, 1);
      next.splice(toIdx, 0, moved);
      return [...next, ...homework];
    });
    markDirty();
  };

  // `pick` — плитка палитры: тип или его вариант («True / False» = true_false_ng без Not Given).
  const addBlock = (sectionId: string, pick: string, atIndex?: number) => {
    const def = blockDefByPick(pick);
    const newId = `bl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setSections((s) =>
      s.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const next = [...sec.blocks];
        next.splice(atIndex ?? sec.blocks.length, 0, {
          id: newId,
          type: def.type,
          content: def.initialContent(),
          order_index: atIndex ?? sec.blocks.length,
        });
        return { ...sec, blocks: reindex(next) };
      }),
    );
    setEditingBlockId(newId); // new block opens its editor so the admin fills it in
    markDirty();
  };

  // Append AI-generated block drafts to a section (each carries its own content).
  const appendGeneratedBlocks = (
    sectionId: string,
    gen: { type: BlockType; content: Record<string, unknown> }[],
  ) => {
    if (gen.length === 0) return;
    setSections((s) =>
      s.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const base = sec.blocks.length;
        const additions: DraftBlock[] = gen.map((g, i) => ({
          id: `bl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}-${i}`,
          type: g.type,
          content: g.content,
          order_index: base + i,
        }));
        return { ...sec, blocks: reindex([...sec.blocks, ...additions]) };
      }),
    );
    markDirty();
  };

  const moveBlockBy = (sectionId: string, blockId: string, delta: number) => {
    setSections((s) =>
      s.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const idx = sec.blocks.findIndex((b) => b.id === blockId);
        const to = idx + delta;
        if (idx < 0 || to < 0 || to >= sec.blocks.length) return sec;
        const next = [...sec.blocks];
        const [moved] = next.splice(idx, 1);
        next.splice(to, 0, moved);
        return { ...sec, blocks: reindex(next) };
      }),
    );
    markDirty();
  };

  // Drag-and-drop reorder: move block `fromId` to sit at `toIdx`.
  const reorderBlock = (sectionId: string, fromId: string, toIdx: number) => {
    setSections((s) =>
      s.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const from = sec.blocks.findIndex((b) => b.id === fromId);
        if (from < 0 || toIdx < 0 || toIdx >= sec.blocks.length || from === toIdx) return sec;
        const next = [...sec.blocks];
        const [moved] = next.splice(from, 1);
        next.splice(toIdx, 0, moved);
        return { ...sec, blocks: reindex(next) };
      }),
    );
    markDirty();
  };

  const duplicateBlock = (sectionId: string, blockId: string) => {
    setSections((s) =>
      s.map((sec) => {
        if (sec.id !== sectionId) return sec;
        const idx = sec.blocks.findIndex((b) => b.id === blockId);
        if (idx < 0) return sec;
        const src = sec.blocks[idx];
        const copy: DraftBlock = {
          id: `bl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          type: src.type,
          content: JSON.parse(JSON.stringify(src.content)),
          order_index: idx + 1,
        };
        const next = [...sec.blocks];
        next.splice(idx + 1, 0, copy);
        return { ...sec, blocks: reindex(next) };
      }),
    );
    markDirty();
  };

  const updateBlockContent = (sectionId: string, blockId: string, content: Record<string, unknown>) => {
    setSections((s) =>
      s.map((sec) =>
        sec.id !== sectionId
          ? sec
          : { ...sec, blocks: sec.blocks.map((b) => (b.id === blockId ? { ...b, content } : b)) },
      ),
    );
    // Блок правят — прежний отказ сервера про него больше не про этот текст. Уберём
    // сразу, а не через 2,5 с: иначе методист отмечает верный ответ, а красное всё
    // висит, и кажется, что не помогло.
    setBlockFailures((f) => {
      if (!f[blockId]) return f;
      const next = { ...f };
      delete next[blockId];
      return next;
    });
    markDirty();
  };

  // ⚠️ УДАЛЕНИЕ БЛОКА — С ВОПРОСОМ И ОТМЕНОЙ (28.09.2026, lib/lessonUndo.ts). Пока видна плашка «Отменить», блок
  // убран только с экрана: в очередь удалений его id не попадает, автосохранение его не трогает.
  const pendingRemovalRef = useRef(pendingRemoval);
  pendingRemovalRef.current = pendingRemoval;
  const reindexSection = (list: DraftSection[], sectionId: string) =>
    list.map((sec) => (sec.id === sectionId ? { ...sec, blocks: reindex(sec.blocks) } : sec));
  /** Отмена больше невозможна — удаление в очередь на сервер. */
  const commitRemoval = (r: PendingRemoval<DraftBlock> | null) => {
    const ids = removalServerIds(r);
    if (ids.length) setDeletedBlockIds((d) => [...d, ...ids]);
    if (r) markDirty();
  };
  const removeBlock = async (sectionId: string, blockId: string) => {
    if (!(await ask({ message: t('lessonBuilder.confirmDeleteBlock'), confirmLabel: t('lessonBuilder.deleteBlock'), danger: true }))) {
      return;
    }
    // Прежний отложенный — удаляется сразу: плашка одна, отменить можно только последний.
    commitRemoval(pendingRemovalRef.current);
    const { sections: next, removed } = takeBlock<DraftBlock, DraftSection>(sections, sectionId, blockId);
    if (!removed) return;
    setSections(reindexSection(next, sectionId));
    setEditingBlockId((cur) => (cur === blockId ? null : cur));
    setPendingRemoval(removed);
    markDirty();
  };
  const undoRemoval = () => {
    const r = pendingRemovalRef.current;
    if (!r) return;
    setSections((cur) => reindexSection(putBack<DraftBlock, DraftSection>(cur, r), r.sectionId));
    setPendingRemoval(null);
    markDirty();
  };
  useEffect(() => {
    if (!pendingRemoval) return;
    const id = setTimeout(() => {
      commitRemoval(pendingRemoval);
      setPendingRemoval(null);
    }, UNDO_MS);
    return () => clearTimeout(id);
    // commitRemoval — новая на каждой отрисовке, а срок считается от удаления, не от отрисовки.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRemoval]);

  // ---------- persistence (diff: deletes → upserts) ----------
  const savingRef = useRef(false);
  /** Сохранение просили, пока шёл проход (кнопка, таймер) — после прохода нужен ещё один. */
  const againRef = useRef(false);
  /**
   * Сохранение урока.
   *
   * ⚠️ ОШИБКА ОДНОГО БЛОКА НЕ ОСТАНАВЛИВАЕТ ОСТАЛЬНЫЕ (техдолг п. 178, 12.09.2026).
   * Раньше `catch` стоял снаружи цикла: первый же отказ бросал всё, что ниже. С
   * появлением серверного сторожа теста (`400 quiz_no_correct_answer`) это значило,
   * что один неотмеченный верный ответ запирал сохранение ВСЕГО урока — вплоть до
   * опечатки в соседнем тексте. Теперь отказ записывается на свой блок, а работа идёт
   * дальше.
   *
   * ⚠️ Отказ РАЗДЕЛА пропускает только его блоки: без созданного раздела их некуда
   * класть. Соседние разделы сохраняются как ни в чём не бывало.
   */
  const handleSave = async () => {
    if (!lessonId) return;
    if (savingRef.current) {
      againRef.current = true;
      return;
    }
    savingRef.current = true;
    // ⚠️ ГОНКА (28.09.2026, разбор удобства админки №6). Правка, пришедшая ПОСЛЕ начала прохода, в него не
    // попадает (он пишет состояние на момент вызова), а таймер автосохранения, сработав посреди прохода, выходил
    // по занятому `savingRef` ни с чем. Итог — «Сохранено» над несохранённой правкой, и она терялась, если больше
    // ничего не трогать. Теперь число правок сверяется до и после, и при расхождении проход повторяется.
    const editsAtStart = editsRef.current;
    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);
    /** Отказы по блокам этого захода. Пустой — значит сохранилось всё. */
    const failures: Record<string, BlockFailure> = {};
    /** Первая беда не про блок: удаление, заголовок урока, раздел. */
    let otherError: string | null = null;
    const fail = (e: unknown) => {
      if (otherError === null) otherError = humanizeError(e);
    };
    try {
      for (const id of deletedBlockIds) {
        try {
          await coursesApi.deleteBlock(id);
          // Снимок удалённого больше ни о чём не говорит: тот же id не вернётся.
          savedSnapRef.current.delete(`blk:${id}`);
          setDeletedBlockIds((d) => d.filter((x) => x !== id));
        } catch (e) {
          // Id остаётся в очереди на удаление — попробуем в следующий раз.
          fail(e);
        }
      }
      for (const id of deletedSectionIds) {
        try {
          await coursesApi.deleteSection(id);
          savedSnapRef.current.delete(`sec:${id}`);
          setDeletedSectionIds((d) => d.filter((x) => x !== id));
        } catch (e) {
          fail(e);
        }
      }
      if (lessonQ.data && lessonQ.data.title !== lessonTitle) {
        try {
          await coursesApi.updateLesson(lessonId, { title: lessonTitle });
        } catch (e) {
          fail(e);
        }
      }
      for (let i = 0; i < sections.length; i++) {
        const sec = sections[i];
        let serverId = sec.serverId;
        if (!serverId) {
          try {
            const created = await coursesApi.createSection({
              lesson_id: lessonId,
              title: sec.title,
              order_index: i,
              is_homework: sec.is_homework ?? false,
            });
            serverId = created.id;
            const tempSectionId = sec.id;
            const committedId = serverId;
            setSections((cur) =>
              cur.map((s) => (s.id === tempSectionId ? { ...s, id: committedId, serverId: committedId } : s)),
            );
            setActiveSectionId((cur) => (cur === tempSectionId ? committedId : cur));
            savedSnapRef.current.set(
              `sec:${committedId}`,
              snapshot({ title: sec.title, order_index: i, is_homework: sec.is_homework ?? false }),
            );
          } catch (e) {
            // Раздела нет — его блокам некуда ложиться. Соседние разделы это не задевает.
            fail(e);
            continue;
          }
        } else {
          const snap = snapshot({ title: sec.title, order_index: i, is_homework: sec.is_homework ?? false });
          if (needsSave(snap, savedSnapRef.current.get(`sec:${serverId}`))) {
            try {
              await coursesApi.updateSection(serverId, {
                title: sec.title,
                order_index: i,
                is_homework: sec.is_homework ?? false,
              });
              savedSnapRef.current.set(`sec:${serverId}`, snap);
            } catch (e) {
              // Раздел на месте, менялся только его заголовок — блоки сохраняем дальше.
              fail(e);
            }
          }
        }

        for (let j = 0; j < sec.blocks.length; j++) {
          const b = sec.blocks[j];
          const bServerId = b.serverId;
          if (!bServerId) {
            const tempBlockId = b.id;
            const parentSectionId = serverId;
            try {
              const created = await coursesApi.createBlock({
                section_id: serverId,
                type: b.type,
                content: b.content,
                order_index: j,
              });
              const committedBlockId = created.id;
              setSections((cur) =>
                cur.map((s) =>
                  s.serverId !== parentSectionId
                    ? s
                    : {
                        ...s,
                        blocks: s.blocks.map((bb) =>
                          bb.id === tempBlockId
                            ? { ...bb, id: committedBlockId, serverId: committedBlockId, content: applyFixes(bb.content) }
                            : bb,
                        ),
                      },
                ),
              );
              setEditingBlockId((cur) => (cur === tempBlockId ? committedBlockId : cur));
              savedSnapRef.current.set(
                `blk:${committedBlockId}`,
                snapshot({ type: b.type, content: applyFixes(b.content), order_index: j }),
              );
            } catch (e) {
              // Блок остался без serverId — в следующий раз его снова попробуют создать,
              // дублей от этого не будет.
              failures[tempBlockId] = blockFailure(e, t);
            }
          } else {
            const snap = snapshot({ type: b.type, content: b.content, order_index: j });
            if (!needsSave(snap, savedSnapRef.current.get(`blk:${bServerId}`))) continue;
            try {
              await coursesApi.updateBlock(bServerId, { type: b.type, content: b.content, order_index: j });
              /**
               * ⚠️ СЕРВЕР ЗАПИСАЛ НЕ ТО, ЧТО МЫ ПОСЛАЛИ: латинские слова с русскими
               * буквами-двойниками он исправляет молча (§5). Снимок берём с
               * ИСПРАВЛЕННОГО, а тем же правилом правим и себя — иначе следующее
               * автосохранение пришлёт старый текст, а плашка будет гореть зря.
               * `PUT` содержимого не возвращает, поэтому считаем сами, а не ждём ответа.
               */
              const fixed = applyFixes(b.content);
              savedSnapRef.current.set(`blk:${bServerId}`, snapshot({ type: b.type, content: fixed, order_index: j }));
              if (fixed !== b.content) {
                const id = b.id;
                setSections((cur) =>
                  cur.map((s) => ({
                    ...s,
                    // Правим то, что в состоянии СЕЙЧАС: методист мог дописать, пока шёл запрос.
                    blocks: s.blocks.map((bb) => (bb.id === id ? { ...bb, content: applyFixes(bb.content) } : bb)),
                  })),
                );
              }
            } catch (e) {
              failures[b.id] = blockFailure(e, t);
            }
          }
        }
      }
      await qc.invalidateQueries({ queryKey: ['admin', 'lesson', lessonId] });
      qc.invalidateQueries({ queryKey: ['admin', 'marathon'] });
    } catch (e: unknown) {
      // Сюда попадает только то, что не поймали по месту, — например обновление кэша.
      fail(e);
    } finally {
      const failed = Object.keys(failures).length;
      setBlockFailures(failures);
      setSaveError(
        failed > 0 ? t('lessonBuilder.savedPartly', { count: failed }) : otherError,
      );
      setSaveStatus(failed > 0 || otherError !== null ? 'unsaved' : 'saved');
      savingRef.current = false;
      setSaving(false);
      if (editsRef.current !== editsAtStart || againRef.current) {
        againRef.current = false;
        setSaveStatus('unsaved');
        // Перезапуск таймера автосохранения: он позовёт свежий `handleSave` со свежим состоянием.
        setDirtyTick((n) => n + 1);
      }
    }
  };

  // Debounced autosave: any mutation bumps dirtyTick, which (re)arms a 2.5s
  // timer. saveRef keeps the timer calling the LATEST handleSave (fresh state).
  const saveRef = useRef(handleSave);
  saveRef.current = handleSave;
  useEffect(() => {
    if (dirtyTick === 0) return;
    const id = setTimeout(() => saveRef.current(), 2500);
    return () => clearTimeout(id);
  }, [dirtyTick]);

  // ---------- копировать / переместить (договор constructor-v2.md §2) ----------
  const [transfer, setTransfer] = useState<{ kind: 'block' | 'section'; op: TransferOp; id: string } | null>(null);

  /**
   * Состояние, УЖЕ отрисованное на экране.
   *
   * ⚠️ После `await handleSave()` замыкание держит `sections` и `blockFailures`
   * с момента нажатия: новые id и отказы записи ещё не видны. Судить «сохранилось
   * ли» по ним нельзя — поэтому смотрим сюда, после отрисовки.
   */
  const committedRef = useRef({ sections, blockFailures, saving, saveStatus });
  useEffect(() => {
    committedRef.current = { sections, blockFailures, saving, saveStatus };
  });

  const waitSaveIdle = async () => {
    const until = Date.now() + 60_000;
    do {
      await new Promise((r) => setTimeout(r, 50));
    } while ((savingRef.current || committedRef.current.saving) && Date.now() < until);
  };

  /**
   * ⚠️ УХОД СО СТРАНИЦЫ — С ДОПИСЬЮ (28.09.2026, разбор удобства админки №6). Автосохранение ждёт 2,5 с после
   * последней правки: «Назад» или переход по меню в эти секунды уносили правку с собой. Теперь переход внутри
   * приложения сперва дописывает урок (и отложенное удаление блока), и только потом уходит; не дописалось —
   * спрашиваем. Закрытие вкладки подождать нельзя — там системное предупреждение браузера.
   *
   * Смена урока без ухода (другой `lessonId` в том же конструкторе) — тоже переход, и перехватывается так же.
   */
  const leaveRisky = saveStatus === 'unsaved' || saving || pendingRemoval !== null;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => leaveRisky && currentLocation.pathname !== nextLocation.pathname,
  );
  const leavingRef = useRef(false);
  useEffect(() => {
    if (blocker.state !== 'blocked' || leavingRef.current) return;
    leavingRef.current = true;
    void (async () => {
      commitRemoval(pendingRemovalRef.current);
      setPendingRemoval(null);
      await waitSaveIdle(); // и отрисовка с очередью удалений — `saveRef` ниже возьмёт свежее состояние
      await saveRef.current();
      await waitSaveIdle();
      const done = committedRef.current.saveStatus === 'saved' || committedRef.current.saveStatus === 'idle';
      const leave =
        done ||
        (await ask({
          title: t('lessonBuilder.leaveUnsavedTitle'),
          message: t('lessonBuilder.leaveUnsaved'),
          confirmLabel: t('lessonBuilder.leaveUnsavedConfirm'),
          cancelLabel: t('lessonBuilder.leaveUnsavedStay'),
          danger: true,
        }));
      leavingRef.current = false;
      if (leave) blocker.proceed?.();
      else blocker.reset?.();
    })();
    // Один перехват на одну попытку ухода; остальное читается из ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker]);
  useEffect(() => {
    if (!leaveRisky) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [leaveRisky]);

  /**
   * Перед переносом — сохранить урок и убедиться, что узел на сервере целиком.
   *
   * ⚠️ СЕРВЕР ПЕРЕНОСИТ ТО, ЧТО ЛЕЖИТ У НЕГО. Несохранённая правка уехала бы
   * старой версией, а после перечитывания урока пропала бы и с экрана.
   *
   * ⚠️ Идущее сохранение сперва дожидаемся: `handleSave` при занятом `savingRef`
   * просто выходит, и перенос ушёл бы поверх недописанного.
   *
   * ⚠️ Узел из урока здесь НЕ убирается через `removeBlock` / `removeSection`:
   * они ставят id в очередь удаления, а перемещение сохраняет id — следующее
   * автосохранение удалило бы уже перенесённое. Урок перечитывается с сервера.
   */
  const prepareTransfer = async (kind: 'block' | 'section', id: string): Promise<string | null> => {
    await waitSaveIdle();
    await handleSave();
    await waitSaveIdle();
    const reason = transferBlocker(kind, id, committedRef.current.sections, committedRef.current.blockFailures);
    if (reason === 'unsaved') return t('lessonBuilder.transferNeedsSave');
    if (reason === 'failed') return t('lessonBuilder.transferFailedBlocks');
    if (reason === 'missing') return t('lessonBuilder.transferMissing');
    return null;
  };

  const afterTransfer = async (op: TransferOp, targetLessonId?: string) => {
    qc.invalidateQueries({ queryKey: ['admin', 'marathon'] });
    if (!lessonId || !needsReload(op, lessonId, targetLessonId)) return;
    const keep = activeSectionId;
    setEditingBlockId(null);
    setBlockFailures({});
    const fresh = await qc.fetchQuery({
      queryKey: ['admin', 'lesson', lessonId],
      queryFn: () => coursesApi.getFullLesson(lessonId),
      staleTime: 0,
    });
    hydrateRef.current(fresh, keep);
  };

  const blockCategories = useBlockCategories();
  const numbers = useMemo(() => computeNumbers(sections), [sections]);
  const activeSection = sections.find((s) => s.id === activeSectionId) ?? null;

  // ---------- предпросмотр ученика (D39) ----------
  const [preview, setPreview] = useState(false);
  // Из черновика, а не с сервера: методист проверяет только что сделанную правку.
  const previewSections = useMemo(() => toPreviewSections(sections, lessonId), [sections, lessonId]);
  /** Куда прокрутить после возврата из предпросмотра — холст ещё не отрисован. */
  const scrollToBlockRef = useRef<string | null>(null);

  /**
   * «Редактировать» в предпросмотре: назад в редактор, к нужному разделу и блоку.
   *
   * Текст, статья и выноска правятся прямо на блоке — у них нет формы «Настроить»,
   * поэтому их не открываем, только прокручиваем.
   */
  const editFromPreview = (sectionId: string, blockId: string | null) => {
    setPreview(false);
    setActiveSectionId(sectionId);
    if (!blockId) return;
    const block = sections.find((s) => s.id === sectionId)?.blocks.find((b) => b.id === blockId);
    if (block && !INLINE_TYPES.has(block.type)) setEditingBlockId(blockId);
    scrollToBlockRef.current = blockId;
  };

  useEffect(() => {
    if (preview || !scrollToBlockRef.current) return;
    const id = scrollToBlockRef.current;
    scrollToBlockRef.current = null;
    requestAnimationFrame(() => {
      document.querySelector(`[data-block-id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, [preview, activeSectionId]);

  return (
    <div className="-mx-4 -my-4 flex min-h-[calc(100vh-2rem)] flex-col md:-mx-6 md:-my-6">
      {/* Top bar */}
      {/* flex-wrap: на 390 кнопки шапки не влезают в строку — «Как у ученика» уходила
          за край экрана. На компьютере шапка по-прежнему одной строкой. */}
      <header className="flex flex-wrap items-center gap-3 border-b border-outline-variant/40 bg-surface-container-lowest px-4 py-2.5">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <span className="material-symbols-outlined">arrow_back</span>
          {t('lessonBuilder.back')}
        </Button>
        <input
          value={lessonTitle}
          onChange={(e) => {
            setLessonTitle(e.target.value);
            markDirty();
          }}
          className="min-w-0 flex-1 rounded-button bg-transparent px-2 py-1 text-sm font-semibold text-on-surface outline-none focus:bg-surface-container"
        />
        <SaveIndicator status={saveStatus} t={t} />
        <Button
          variant="secondary"
          size="sm"
          disabled={!activeSection}
          onClick={() => setGenOpen(true)}
          title={t('lessonBuilder.generateTitle')}
        >
          <span className="material-symbols-outlined">auto_awesome</span>
          {t('lessonBuilder.generate')}
        </Button>
        {/* «Как у ученика» — предпросмотр прямо здесь, из черновика, отвечать можно и
            ничего не сохраняется (D39). Страница ученика в новой вкладке осталась
            значком: там сохранённая версия и настоящий урок со стартом и пульсом. */}
        <Button
          variant={preview ? 'primary' : 'secondary'}
          size="sm"
          disabled={!activeSection}
          onClick={() => setPreview((p) => !p)}
          title={t('lessonBuilder.previewToggleTitle')}
        >
          <span className="material-symbols-outlined">{preview ? 'edit' : 'visibility'}</span>
          {preview ? t('lessonBuilder.previewBack') : t('lessonBuilder.previewStudent')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={!lessonId}
          onClick={() => lessonId && window.open(`/app/lessons/${lessonId}`, '_blank', 'noopener')}
          title={t('lessonBuilder.previewTitle')}
          aria-label={t('lessonBuilder.previewTitle')}
        >
          <span className="material-symbols-outlined">open_in_new</span>
        </Button>
        <Button size="sm" onClick={handleSave} loading={saving}>
          <span className="material-symbols-outlined">save</span>
          {isTemplate ? t('lessonBuilder.saveInTemplate') : t('common.save')}
        </Button>

        {/*
          ⚠️ КНОПКА ЕСТЬ ТОЛЬКО У ОБРАЗЦА. Правка образца сама по себе никуда не
          уезжает: методист правит сколько нужно и сам решает, когда это увидят
          курсы. Раскатка отдельным нажатием — чтобы недоделанная правка не
          оказалась у учеников.

          ⚠️ И самое важное про уроки: раскатка МЕНЯЕТ СОДЕРЖАНИЕ, но НЕ
          ОТКРЫВАЕТ уроки. Урок, добавленный в образец, приезжает во все курсы
          черновиком — открывает его куратор своей группы. Поэтому сентябрьский
          поток получит правку сразу, а октябрьский останется закрытым.
        */}
        {isTemplate && (
          <Button
            size="sm"
            variant="secondary"
            loading={applying}
            onClick={handleApplyToCourses}
            title={t('lessonBuilder.applyHint')}
          >
            <span className="material-symbols-outlined">sync</span>
            {t('lessonBuilder.applyToCourses')}
          </Button>
        )}
      </header>

      {/* Brand banner */}
      <div className="bg-primary px-4 py-1.5 text-center text-xs font-semibold text-on-primary">
        {t('lessonBuilder.banner')}
      </div>

      {saveError && (
        <div className="bg-error-container px-4 py-2 text-center text-sm text-on-error-container">{saveError}</div>
      )}

      {/* Body */}
      <div className="grid flex-1 grid-cols-[56px_1fr] gap-0 lg:grid-cols-[56px_1fr_280px]">
        {/* Left icon palette */}
        <aside className="border-r border-outline-variant/40 bg-surface-container-lowest py-3">
          <Palette
            categories={blockCategories}
            // В предпросмотре блок добавился бы невидимо — холст сейчас показывает ученика.
            disabled={!activeSection || preview}
            onPick={(type) => activeSection && addBlock(activeSection.id, type)}
          />
        </aside>

        {/* Center canvas */}
        <main className="min-w-0 overflow-y-auto px-4 py-6 md:px-8">
          {lessonQ.isLoading ? (
            <p className="text-sm text-on-surface-variant">{t('lessonBuilder.loadingLesson')}</p>
          ) : !activeSection ? (
            <div className="mx-auto max-w-3xl text-center">
              <p className="mb-4 text-sm text-on-surface-variant">{t('lessonBuilder.noSections')}</p>
              <Button onClick={addSection}>
                <span className="material-symbols-outlined">add</span>
                {t('lessonBuilder.addSection')}
              </Button>
            </div>
          ) : preview ? (
            <StudentPreview
              sections={previewSections}
              activeSectionId={activeSectionId}
              onSelectSection={setActiveSectionId}
              onEdit={editFromPreview}
            />
          ) : (
            <div className="mx-auto max-w-3xl">
              <SectionCanvas
                key={activeSection.id}
                section={activeSection}
                numbers={numbers}
                categories={blockCategories}
                editingBlockId={editingBlockId}
                setEditingBlockId={setEditingBlockId}
                onRename={(title) => renameSection(activeSection.id, title)}
                onRemove={() => removeSection(activeSection.id)}
                onAddBlock={(type, at) => addBlock(activeSection.id, type, at)}
                onMoveBlock={(id, d) => moveBlockBy(activeSection.id, id, d)}
                onReorder={(fromId, toIdx) => reorderBlock(activeSection.id, fromId, toIdx)}
                onDuplicate={(id) => duplicateBlock(activeSection.id, id)}
                onUpdateContent={(id, c) => updateBlockContent(activeSection.id, id, c)}
                onRemoveBlock={(id) => removeBlock(activeSection.id, id)}
                onTransferSection={(op) =>
                  activeSection.serverId && setTransfer({ kind: 'section', op, id: activeSection.serverId })
                }
                onTransferBlock={(id, op) => setTransfer({ kind: 'block', op, id })}
                failures={blockFailures}
              />
            </div>
          )}
        </main>

        {/* Right sections nav */}
        <aside className="hidden border-l border-outline-variant/40 bg-surface p-3 lg:block">
          <SectionsNav
            sections={sections}
            activeId={activeSectionId ?? undefined}
            mode="edit"
            onSelect={setActiveSectionId}
            onAddSection={addSection}
            onAddHomework={addHomeworkSection}
            onReorder={reorderSection}
          />
        </aside>
      </div>

      {transfer && (
        <TransferDialog
          kind={transfer.kind}
          op={transfer.op}
          sourceId={transfer.id}
          source={{ marathonId, lessonId, sectionId: activeSection?.serverId, isTemplate }}
          beforeRun={() => prepareTransfer(transfer.kind, transfer.id)}
          onDone={(r) => afterTransfer(r.op, r.targetLessonId)}
          onClose={() => setTransfer(null)}
        />
      )}

      {genOpen && activeSection && (
        <GenerateLessonDialog
          onClose={() => setGenOpen(false)}
          onGenerated={(blocks) => {
            appendGeneratedBlocks(activeSection.id, blocks);
            setGenOpen(false);
          }}
        />
      )}

      {pendingRemoval && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-4 rounded-xl bg-inverse-surface px-4 py-3 text-sm text-inverse-on-surface shadow-lg"
        >
          <span>{t('lessonBuilder.blockRemoved')}</span>
          <button type="button" onClick={undoRemoval} className="font-semibold text-inverse-primary">
            {t('lessonBuilder.undoRemove')}
          </button>
        </div>
      )}
    </div>
  );
}

// =============================================================
// GenerateLessonDialog — AI lesson draft from a topic
// =============================================================

function GenerateLessonDialog({
  onClose,
  onGenerated,
}: {
  onClose: () => void;
  onGenerated: (blocks: { type: BlockType; content: Record<string, unknown> }[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState('');
  const [level, setLevel] = useState('B1');
  const [count, setCount] = useState(6);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const langName = i18n.language === 'kk' ? 'Kazakh' : i18n.language === 'en' ? 'English' : 'Russian';

  const submit = async () => {
    if (!topic.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const blocks = await coursesApi.generateLesson({ topic: topic.trim(), level, language: langName, count });
      if (blocks.length === 0) {
        setError(t('lessonBuilder.genEmpty'));
        return;
      }
      onGenerated(blocks);
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-inverse-surface/40 p-4" onMouseDown={onClose}>
      <div
        className="w-full max-w-md space-y-4 rounded-card border border-outline-variant/50 bg-surface p-5 shadow-ambient"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">auto_awesome</span>
          <h2 className="font-display text-lg font-semibold text-on-surface">{t('lessonBuilder.generate')}</h2>
        </div>
        <p className="text-sm text-on-surface-variant">{t('lessonBuilder.genHint')}</p>

        <div className="space-y-1">
          <label className="text-xs font-medium text-on-surface-variant">{t('lessonBuilder.genTopic')}</label>
          <input
            autoFocus
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder={t('lessonBuilder.genTopicPlaceholder')}
            className="w-full rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-on-surface-variant">{t('lessonBuilder.genLevel')}</label>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="w-full rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {['A1', 'A2', 'B1', 'B2', 'C1'].map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>
          <div className="flex-1 space-y-1">
            <label className="text-xs font-medium text-on-surface-variant">{t('lessonBuilder.genCount')}</label>
            <select
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-full rounded-button bg-surface-container-low p-2.5 text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {[4, 6, 8, 10].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {error && <p className="text-sm text-error">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} loading={busy} disabled={!topic.trim()}>
            <span className="material-symbols-outlined">auto_awesome</span>
            {t('lessonBuilder.genSubmit')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// SaveIndicator
// =============================================================

function SaveIndicator({ status, t }: { status: 'idle' | 'unsaved' | 'saving' | 'saved'; t: (k: string) => string }) {
  if (status === 'idle') return null;
  const map = {
    unsaved: { icon: 'cloud_off', text: t('lessonBuilder.unsaved'), cls: 'text-on-surface-variant' },
    saving: { icon: 'cloud_sync', text: t('lessonBuilder.saving'), cls: 'text-primary' },
    saved: { icon: 'cloud_done', text: t('lessonBuilder.saved'), cls: 'text-success' },
  } as const;
  const s = map[status];
  return (
    <span className={cn('hidden items-center gap-1 text-xs sm:flex', s.cls)}>
      <span className="material-symbols-outlined text-base">{s.icon}</span>
      {s.text}
    </span>
  );
}

// =============================================================
// SectionCanvas — active section title + blocks + "+"
// =============================================================

function SectionCanvas({
  section,
  numbers,
  categories,
  editingBlockId,
  setEditingBlockId,
  onRename,
  onRemove,
  onAddBlock,
  onMoveBlock,
  onReorder,
  onDuplicate,
  onUpdateContent,
  onRemoveBlock,
  onTransferSection,
  onTransferBlock,
  failures,
}: {
  section: DraftSection;
  /** «Копировать в…» / «Переместить в…» раздела и блока — id серверный. */
  onTransferSection: (op: TransferOp) => void;
  onTransferBlock: (serverId: string, op: TransferOp) => void;
  numbers: Map<string, string>;
  categories: { id: BlockCategory; label: string }[];
  /** Отказы сервера по блокам — ключ совпадает с `block.id` черновика. */
  failures: Record<string, BlockFailure>;
  editingBlockId: string | null;
  setEditingBlockId: (id: string | null) => void;
  onRename: (title: string) => void;
  onRemove: () => void;
  onAddBlock: (pick: string, at?: number) => void;
  onMoveBlock: (id: string, delta: number) => void;
  onReorder: (fromId: string, toIdx: number) => void;
  onDuplicate: (id: string) => void;
  onUpdateContent: (id: string, content: Record<string, unknown>) => void;
  onRemoveBlock: (id: string) => void;
}) {
  const ask = useConfirm();
  const { t } = useTranslation();
  const [overIdx, setOverIdx] = useState<number | null>(null);

  return (
    <div>
      {/* Section title */}
      <div className="mb-4 flex items-center gap-2">
        {section.is_homework && <span className="material-symbols-outlined text-tertiary">home</span>}
        <input
          value={section.title}
          onChange={(e) => onRename(e.target.value)}
          placeholder={t('lessonBuilder.sectionTitlePlaceholder')}
          className="flex-1 rounded-button bg-transparent px-1 py-1 font-display text-2xl font-bold tracking-display text-on-surface outline-none focus:bg-surface-container"
        />
        <TransferMenu
          onPick={onTransferSection}
          disabledReason={section.serverId ? null : t('lessonBuilder.transferNeedsSave')}
        />
        <button
          onClick={async () => {
            if (await ask({ message: t('lessonBuilder.confirmDeleteSection'), danger: true })) onRemove();
          }}
          className="rounded-button p-1.5 text-on-surface-variant hover:bg-error-container hover:text-on-error-container"
          title={t('lessonBuilder.deleteSection')}
        >
          <span className="material-symbols-outlined">delete</span>
        </button>
      </div>

      <AddBlockDivider categories={categories} onPick={(type) => onAddBlock(type, 0)} />

      {section.blocks.length === 0 ? (
        <p className="py-8 text-center text-sm text-on-surface-variant">{t('lessonBuilder.emptySection')}</p>
      ) : (
        section.blocks.map((block, idx) => (
          <div
            key={block.id}
            // По этой метке «Редактировать» из предпросмотра прокручивает к блоку.
            data-block-id={block.id}
            onDragOver={(e) => {
              if (e.dataTransfer.types.includes('application/x-block-id')) {
                e.preventDefault();
                setOverIdx(idx);
              }
            }}
            onDragLeave={() => setOverIdx((o) => (o === idx ? null : o))}
            onDrop={(e) => {
              const fromId = e.dataTransfer.getData('application/x-block-id');
              setOverIdx(null);
              if (fromId) {
                e.preventDefault();
                onReorder(fromId, idx);
              }
            }}
            className={cn('rounded-card', overIdx === idx && 'ring-2 ring-primary')}
          >
            <BuilderBlock
              block={block}
              number={numbers.get(block.id)}
              editing={editingBlockId === block.id}
              onToggleEdit={() => setEditingBlockId(editingBlockId === block.id ? null : block.id)}
              canUp={idx > 0}
              canDown={idx < section.blocks.length - 1}
              onMove={(d) => onMoveBlock(block.id, d)}
              onDuplicate={() => onDuplicate(block.id)}
              onChange={(c) => onUpdateContent(block.id, c)}
              onRemove={() => onRemoveBlock(block.id)}
              onTransfer={(op) => block.serverId && onTransferBlock(block.serverId, op)}
              failure={failures[block.id]}
            />
            <AddBlockDivider categories={categories} onPick={(type) => onAddBlock(type, idx + 1)} />
          </div>
        ))
      )}
    </div>
  );
}

// =============================================================
// BuilderBlock — preview + hover ⋯ menu + inline editor
// =============================================================

function BuilderBlock({
  block,
  number,
  editing,
  onToggleEdit,
  canUp,
  canDown,
  onMove,
  onDuplicate,
  onChange,
  onRemove,
  onTransfer,
  failure,
}: {
  block: DraftBlock;
  onTransfer: (op: TransferOp) => void;
  number?: string;
  editing: boolean;
  onToggleEdit: () => void;
  canUp: boolean;
  canDown: boolean;
  onMove: (delta: number) => void;
  onDuplicate: () => void;
  onChange: (content: Record<string, unknown>) => void;
  onRemove: () => void;
  /** Чем сервер ответил именно на этот блок при последнем сохранении. */
  failure?: BlockFailure;
}) {
  const { t } = useTranslation();
  const def = blockDefForBlock(block.type, block.content);
  /**
   * Что сервер не примет — то же правило, что у него самого (договор §1).
   *
   * ⚠️ СЧИТАЕТСЯ ДО СОХРАНЕНИЯ И НИЧЕГО НЕ ЗАПРЕЩАЕТ. Запрет держит сервер, и он
   * намеренно пропускает НЕИЗМЕНЁННЫЙ старый тест (§1.4): запрети мы сохранение во
   * фронте — урок со старым битым тестом нельзя было бы сохранить вовсе, даже правя
   * в нём опечатку.
   */
  const guard = blockGuardIssue(block.type, block.content);
  /**
   * Латинские слова с русскими буквами-двойниками (договор §5.2).
   *
   * ⚠️ Сервер исправит их и без нас — молча, при записи. Показываем затем, чтобы
   * методист УВИДЕЛ свою раскладку: иначе он наберёт так же в следующем задании, в
   * словаре и в названии курса, куда сторож не дотягивается.
   */
  const twins = scanContent(block.content);
  const twinsToFix = willBeFixed(twins);
  const twinsToCheck = needsHumanLook(twins);
  if (!def) return null;
  // Text-family blocks are edited directly on the block (Edvibe-style typing in
  // place) — no preview/Configure round-trip; their editor IS a plain text field.
  const inline = INLINE_TYPES.has(block.type);

  return (
    <div className="group relative rounded-card border border-outline-variant/40 bg-surface p-4 transition hover:border-outline-variant">
      {/* Header row: drag handle + number + type + actions */}
      <div className="mb-2 flex items-center gap-2">
        <span
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/x-block-id', block.id);
            e.dataTransfer.effectAllowed = 'move';
          }}
          title={t('lessonBuilder.dragToReorder')}
          className="cursor-grab text-on-surface-variant opacity-40 transition hover:opacity-100 active:cursor-grabbing"
        >
          <span className="material-symbols-outlined text-base">drag_indicator</span>
        </span>
        {number ? (
          <span className="inline-flex items-center rounded-button bg-primary-container px-2 py-0.5 font-mono text-xs font-bold text-on-primary-container">
            {number}
          </span>
        ) : (
          <span className="material-symbols-outlined text-base text-on-surface-variant">{def.icon}</span>
        )}
        <span className="flex-1 truncate text-xs font-semibold text-on-surface-variant">{t(def.labelKey)}</span>

        <div className="flex items-center gap-0.5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
          <IconBtn icon="arrow_upward" title={t('lessonBuilder.moveUp')} disabled={!canUp} onClick={() => onMove(-1)} />
          <IconBtn icon="arrow_downward" title={t('lessonBuilder.moveDown')} disabled={!canDown} onClick={() => onMove(1)} />
          <IconBtn icon="content_copy" title={t('lessonBuilder.duplicate')} onClick={onDuplicate} />
          <TransferMenu onPick={onTransfer} disabledReason={block.serverId ? null : t('lessonBuilder.transferNeedsSave')} />
          {!inline && (
            <IconBtn
              icon={editing ? 'check' : 'tune'}
              title={editing ? t('lessonBuilder.done') : t('lessonBuilder.settings')}
              active={editing}
              onClick={onToggleEdit}
            />
          )}
          <IconBtn icon="delete" title={t('lessonBuilder.deleteBlock')} danger onClick={onRemove} />
        </div>
      </div>

      {/* Сервер отказался записать именно этот блок. Красным и с номером вопроса:
          общая полоса наверху в уроке на сорок заданий не говорит, где чинить. */}
      {failure && (
        <p role="alert" className="mb-2 rounded-button bg-error-container px-3 py-2 text-[12.5px] text-on-error-container">
          {failure.text}
        </p>
      )}

      {/* Подсказка ДО сохранения: задание не станет проходимым. Жёлтым, а не
          красным, — это ещё не отказ, а предупреждение; сохранению не мешает. */}
      {!failure && guard && (
        <p className="mb-2 rounded-button bg-tertiary-container px-3 py-2 text-[12.5px] text-on-tertiary-container">
          {guard.kind === 'quiz_no_correct'
            ? t('lessonBuilder.hintQuizNoCorrect', { numbers: issueNumbers(guard.indexes) })
            : t('lessonBuilder.hintTfForeign', { numbers: issueNumbers(guard.indexes) })}
        </p>
      )}

      {/* Русские буквы-двойники в латинских словах. Сервер исправит их сам при записи,
          но методист должен увидеть это ДО сохранения — иначе наберёт так же снова. */}
      {twinsToFix.length > 0 && (
        <div className="mb-2 rounded-button bg-surface-container px-3 py-2 text-[12.5px] text-on-surface-variant">
          <div className="mb-1 flex items-center justify-between gap-2">
            <span className="font-semibold">{t('lessonBuilder.twinsFixTitle')}</span>
            <button
              type="button"
              onClick={() => onChange(applyFixes(block.content))}
              className="shrink-0 font-medium text-primary hover:underline"
            >
              {t('lessonBuilder.twinsFixAll')}
            </button>
          </div>
          <ul className="space-y-0.5">
            {twinsToFix.slice(0, 10).map((f, i) => (
              <li key={`${f.path}-${i}`} className="flex flex-wrap items-center gap-x-1.5">
                {/* Русская буква подсвечена и названа кодом: глазом её от латинской
                    не отличить, и без этого строка читалась бы как «x → x». */}
                <code className="font-mono">
                  {splitTwins(f.was).map((part, k) =>
                    part.twin ? (
                      <span key={k} className="rounded-sm bg-error-container px-0.5 text-on-error-container">
                        {part.text}
                      </span>
                    ) : (
                      <span key={k}>{part.text}</span>
                    ),
                  )}
                </code>
                <span aria-hidden>→</span>
                <code className="font-mono text-on-surface">{f.becomes}</code>
                <span className="text-on-surface-variant/70">
                  {splitTwins(f.was)
                    .filter((p) => p.twin)
                    .map((p) => `${p.text} ${codeOf(p.text)} → ${p.becomes}`)
                    .join(', ')}
                </span>
                <span className="truncate text-on-surface-variant/70">{f.path}</span>
              </li>
            ))}
          </ul>
          {twinsToFix.length > 10 && <p className="mt-1">{t('lessonBuilder.twinsMore', { count: twinsToFix.length - 10 })}</p>}
        </div>
      )}

      {/* Сервер эти слова НЕ трогает — решает методист. Поэтому без кнопки. */}
      {twinsToCheck.length > 0 && (
        <p className="mb-2 rounded-button bg-surface-container px-3 py-2 text-[12.5px] text-on-surface-variant">
          {t('lessonBuilder.twinsCheckLayout', { words: twinsToCheck.slice(0, 5).map((f) => f.was).join(', ') })}
        </p>
      )}

      {/* Inline-editable text blocks: the field is the block. */}
      {inline ? (
        def.renderEditor(block.content, onChange)
      ) : (
        <>
          {/* Real preview (student view, inert). Click to open the editor. */}
          {!editing && (
            <button type="button" onClick={onToggleEdit} className="block w-full cursor-text text-left" title={t('lessonBuilder.editBlock')}>
              {/* Mirror the lesson's per-block title above the preview so the
                  canvas shows exactly what the student will see. */}
              {titleText(block.content.title) && (
                <h3
                  className="mb-1.5 font-display text-base font-bold tracking-display text-on-surface"
                  dangerouslySetInnerHTML={{ __html: titleHtml(block.content.title) }}
                />
              )}
              <BlockBoundary
                resetKey={block.content}
                fallback={<p className="py-3 text-sm text-on-surface-variant">{t('lessonBuilder.previewBlockError')}</p>}
              >
                <BlockView block={block as Block} mode="preview" />
              </BlockBoundary>
            </button>
          )}

          {/* Inline editor (legacy per-type form) */}
          {editing && (
            <div className="space-y-2 rounded-button bg-surface-container-lowest p-3">
              {/* Optional per-block title, shown next to the "4.1" number in the
                  lesson. Available on EVERY block type; leave blank to omit.
                  Форматируется как текст — курсив, подчёркивание, цвет, заливка (D39).
                  Жирного нет: заголовок у ученика и так 700 (решение владельца 13.09). */}
              <RichTextEditor
                singleLine
                minRows={1}
                value={(block.content.title as string) ?? ''}
                onChange={(v) => onChange({ ...block.content, title: v })}
                placeholder={t('lessonBuilder.titlePlaceholder')}
              />
              {/* Shared, rich "task text" field for every EXERCISE type (fill /
                  quiz / order cover all interactive blocks): formatting +
                  paragraphs, ⌘B/I/U. Text blocks carry their own rich prose field
                  and structural/media blocks (divider/link/embed/pdf…) have no
                  task text, so both opt out here. */}
              {['fill', 'quiz', 'order'].includes(def.category) && (
                <RichTextEditor
                  value={(block.content.instruction as string) ?? ''}
                  onChange={(v) => onChange({ ...block.content, instruction: v })}
                  placeholder={t('lessonBuilder.instructionPlaceholder')}
                />
              )}
              {def.renderEditor(block.content, onChange)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IconBtn({
  icon,
  title,
  onClick,
  disabled,
  danger,
  active,
}: {
  icon: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        'rounded-button p-1 text-on-surface-variant transition disabled:opacity-30',
        active && 'bg-primary-container text-on-primary-container',
        danger ? 'hover:bg-error-container hover:text-on-error-container' : 'hover:bg-surface-container-high',
      )}
    >
      <span className="material-symbols-outlined text-base">{icon}</span>
    </button>
  );
}

// =============================================================
// Add-block affordances
// =============================================================

/** A subtle "+" divider between blocks that opens the block palette popover. */
function AddBlockDivider({ categories, onPick }: { categories: { id: BlockCategory; label: string }[]; onPick: (pick: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex items-center justify-center py-1">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-full border transition',
          open
            ? 'border-primary bg-primary text-on-primary'
            : 'border-outline-variant/60 bg-surface text-on-surface-variant opacity-40 hover:opacity-100',
        )}
      >
        <span className="material-symbols-outlined text-base">add</span>
      </button>
      {open && (
        <BlockPickerPanel
          categories={categories}
          onPick={(type) => {
            onPick(type);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

/** Left icon rail: one icon per category, opens the picker filtered to it. */
function Palette({
  categories,
  disabled,
  onPick,
}: {
  categories: { id: BlockCategory; label: string }[];
  disabled?: boolean;
  onPick: (pick: string) => void;
}) {
  const [openCat, setOpenCat] = useState<BlockCategory | null>(null);
  // ⚠️ ТОЛЬКО ЗНАЧКИ ИЗ НАШЕГО УРЕЗАННОГО ШРИФТА.
  //
  // Имя значка превращается в картинку лигатурой. Значка нет в шрифте —
  // лигатура не срабатывает, и на экране появляется САМО ИМЯ заглавными
  // буквами. Так `perm_media`, `swap_vert` и `notes` рисовались надписями
  // PERM_MEDIA, SWAP_VERT и NOTES поверх конструктора (найдено 04.09.2026).
  //
  // Сторож `iconSubset.test.ts` читает сам файл шрифта и ловит такое, но
  // проверял он разметку, а не карты значков вроде этой.
  const catIcon: Record<BlockCategory, string> = {
    media: 'collections',
    fill: 'edit_note',
    quiz: 'quiz',
    order: 'sort',
    text: 'subject',
    misc: 'widgets',
  };
  return (
    <div className="flex flex-col items-center gap-1">
      {categories.map((c) => (
        <div key={c.id} className="relative">
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpenCat((o) => (o === c.id ? null : c.id))}
            title={c.label}
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-button text-on-surface-variant transition disabled:opacity-30',
              openCat === c.id ? 'bg-primary-container text-on-primary-container' : 'hover:bg-surface-container-high',
            )}
          >
            <span className="material-symbols-outlined">{catIcon[c.id] ?? 'widgets'}</span>
          </button>
          {openCat === c.id && (
            <BlockPickerPanel
              category={c.id}
              categories={categories}
              anchorLeft
              onPick={(type) => {
                onPick(type);
                setOpenCat(null);
              }}
              onClose={() => setOpenCat(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/** A single block card in the palette gallery: icon tile + label. */
function BlockCard({ b, onPick }: { b: (typeof blockRegistry)[number]; onPick: (pick: string) => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onPick(paletteKey(b))}
      title={t(b.labelKey)}
      className="flex flex-col items-center gap-1.5 rounded-card border border-outline-variant/50 bg-surface-container-low p-2.5 text-center transition hover:border-primary hover:bg-primary-container/40"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-button bg-surface-container-high text-primary">
        <span className="material-symbols-outlined text-xl">{b.icon}</span>
      </span>
      <span className="line-clamp-2 text-[11px] font-medium leading-tight text-on-surface">{t(b.labelKey)}</span>
    </button>
  );
}

/**
 * The block palette gallery. Renders block types as preview cards grouped by
 * category, with a search box that filters by label across every category
 * (ignoring the scoped `category` while a query is typed).
 */
function BlockPickerPanel({
  categories,
  category,
  onPick,
  onClose,
  anchorLeft,
}: {
  categories: { id: BlockCategory; label: string }[];
  category?: BlockCategory;
  onPick: (pick: string) => void;
  onClose: () => void;
  anchorLeft?: boolean;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [onClose]);

  const q = query.trim().toLowerCase();
  const matches = q ? blockRegistry.filter((b) => t(b.labelKey).toLowerCase().includes(q)) : [];
  // When searching, show a flat grid across all categories; otherwise group.
  const shownCats = category ? categories.filter((c) => c.id === category) : categories;

  return (
    <div
      ref={ref}
      className={cn(
        'absolute z-30 flex max-h-96 w-80 flex-col overflow-hidden rounded-card border border-outline-variant/50 bg-surface shadow-ambient',
        anchorLeft ? 'left-12 top-0' : 'left-1/2 top-9 -translate-x-1/2',
      )}
    >
      <div className="border-b border-outline-variant/40 p-2">
        <div className="flex items-center gap-2 rounded-button bg-surface-container-low px-2.5 py-1.5">
          <span className="material-symbols-outlined text-base text-on-surface-variant">search</span>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('lessonBuilder.searchBlocks')}
            className="w-full bg-transparent text-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="text-on-surface-variant hover:text-on-surface">
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          )}
        </div>
      </div>

      <div className="overflow-y-auto p-2">
        {q ? (
          matches.length === 0 ? (
            <p className="px-1 py-6 text-center text-xs text-on-surface-variant">{t('lessonBuilder.noBlocksFound')}</p>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {matches.map((b) => (
                <BlockCard key={paletteKey(b)} b={b} onPick={onPick} />
              ))}
            </div>
          )
        ) : (
          shownCats.map((cat) => (
            <div key={cat.id} className="mb-3 last:mb-0">
              <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-label text-on-surface-variant">{cat.label}</p>
              <div className="grid grid-cols-3 gap-1.5">
                {blockRegistry
                  .filter((b) => b.category === cat.id)
                  .map((b) => (
                    <BlockCard key={paletteKey(b)} b={b} onPick={onPick} />
                  ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// =============================================================
// Helpers
// =============================================================

function reindex<T extends { order_index: number }>(arr: T[]): T[] {
  return arr.map((it, idx) => ({ ...it, order_index: idx }));
}
